import React, { useEffect, useRef, useState } from "react";
import { CsrAPI } from "../../apis/CsrAPI";
import DashboardContent from "../../components/DashboardContent";
import { FieldGroup, FormArray, FormBuilder } from "react-reactive-form";
import ComparativePKQuestions from "../../questions/ComparativePKQuestions";
import useQuestions from "../../hooks/useQuestions";
import useCsr from "../../hooks/useCsr";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as Sentry from "@sentry/react";
import RedirectHandler from "../../components/RedirectHandler";
import { useParams } from "react-router-dom";
import {
  populateValueHelperArray,
  // extractArrayValues,
} from "./utils/saveRestoreUtils";
import { createUnifiedSubmitHandler } from "../../utils/unifiedFormSubmit";

const isProduction = process.env.NODE_ENV === "production";

const ComparativePharmacokinetics = () => {
  const { studyId } = useParams();
  const { setAjaxLoading, wrongEnvironment } = useCsr();
  const {
    questionsData,
    questionsDataSaved,
    setQuestionsDataSaved,
    qForm,
    wizardForm,
    setFormFilledData,
    setWizardFormFilledData,
  } = useQuestions();

  const formArraysKeys = {
    qEfficacyAnalytes: "object",
    qPrimaryEfficacyComparisonNames: "object",
    qEfficacyPopulations: "object",
    qEfficacyAnalysisTypeNames: "object",
    qEfficacyTimepointNames: "object",
    qEfficacySensitivityNames: "object",
    qEfficacyKaplanMeierAnalytes: "object",
    qTgNamesDoseFinding: "object",
    qTgNames: "object",
    qTgNamesComparison: "object",
    qPrimaryEfficacySensitivityNames: "object",
    qPrimaryEfficacyAnalytes: "object",
    qPrimaryEfficacyPopulation: "object",
    qPrimaryEfficacyAnalysisTypeNames: "object",
    qPrimaryEfficacyAnalytesComparison: "object",
    qPrimaryEfficacyBaseline: "object",
    qPrimaryEfficacyTimepointNames: "object",
    qSecondaryEfficacyAnalytes: "object",
    qSubgroupClassification: "object",
    qPkStdColumn: "object",
    qPkTimelines: "object",
    qPkAnalytes: "object",
  };

  // const formArraysKeysToClean = ["Metabolites"];

  const errRef = useRef();
  const [errMsg, setErrMsg] = useState("");
  const [showRedirect, setShowRedirect] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // Force re-render after restore so qPkTable/qPkPopulation controls are visible in UI
  const [, setRestoreVersion] = useState(0);
  const lastRestoredQuestionsDataRef = useRef(null);

  useEffect(() => {
    if (!questionsDataSaved) {
      setAjaxLoading(true);
      CsrAPI.getQuestions({ studyId })
        .then(questionsDataSaved => {
          replaceNullArrays(questionsDataSaved);
          setQuestionsDataSaved(questionsDataSaved);
          setErrMsg("");
        })
        .catch(error => {
          if (isProduction) {
            Sentry.captureException(error);
          }
          if (!error?.response) {
            setErrMsg("No Server Response");
          } else if (error.response?.status === 401) {
            setErrMsg("Invalid user credentials");
          } else if (error.response?.status === 403) {
            setShowRedirect(true);
          } else {
            setErrMsg("Failed to get questions");
          }
          errRef.current?.focus();
        })
        .finally(() => {
          setAjaxLoading(false);
        });
    }
  }, [studyId, setQuestionsDataSaved, questionsDataSaved]);

  const replaceNullArrays = obj => {
    for (let key in obj) {
      if (typeof obj[key] === "object") {
        if (Array.isArray(obj[key])) {
          if (obj[key].every(value => value === null)) {
            obj[key] = [];
          }
        } else {
          replaceNullArrays(obj[key]);
        }
      }
    }
  };

  useEffect(() => {
    if (!isSaving) {
      retrieveSavedWizardState();
    }
  }, [questionsDataSaved, isSaving]);

  const retrieveSavedWizardState = () => {
    if (isSaving) {
      return;
    }

    if (questionsDataSaved && questionsDataSaved.wizardForm) {
      const wizardFormValue = questionsDataSaved.wizardForm;

      if (lastRestoredQuestionsDataRef.current === questionsDataSaved) {
        return;
      }
      lastRestoredQuestionsDataRef.current = questionsDataSaved;

      // Clear existing form arrays before repopulating
      Object.keys(wizardFormValue).forEach(key => {
        if (formArraysKeys[key] && wizardForm.controls[key]) {
          while (wizardForm.controls[key].length > 0) {
            wizardForm.controls[key].removeAt(0);
          }
        }
      });

      Object.keys(wizardFormValue).map(function (key) {
        if (formArraysKeys[key]) {
          if (formArraysKeys[key] === "object") {
            wizardFormValue[key].forEach(item => {
              wizardForm.controls[key]?.push(FormBuilder.group(item));
            });
          } else {
            wizardFormValue[key].forEach(item => {
              wizardForm.controls[key].push(FormBuilder.control(item));
            });
          }
        } else {
          // Skip Comparative PK flat controls (qPkTable*, qPkPopulation*); they are restored from qPkCompAnalysis below.
          // Patching them here would overwrite user input on every effect run.
          const isCompPkFlatControl =
            /^qPkTable(Comp)?(_\d+)?$/.test(key) ||
            /^qPkPopulation(Comp)?(_\d+)?$/.test(key);
          if (isCompPkFlatControl) {
            return true;
          }
          // Ensure control exists before patching
          if (!wizardForm.contains(key)) {
            wizardForm.addControl(
              key,
              FormBuilder.control(wizardFormValue[key])
            );
          } else {
            wizardForm.patchValue({ [key]: wizardFormValue[key] });
          }
        }
        return true;
      });

      // Ensure qPkCompEnabled control exists
      if (!wizardForm.contains("qPkCompEnabled")) {
        wizardForm.addControl(
          "qPkCompEnabled",
          FormBuilder.control(wizardFormValue.qPkCompEnabled || "false")
        );
      }

      // Ensure qPK control exists
      if (!wizardForm.contains("qPK")) {
        wizardForm.addControl(
          "qPK",
          FormBuilder.control(wizardFormValue.qPK || "false")
        );
      }

      // Ensure qPkStdEnabled control exists (needed for conflict detection)
      if (!wizardForm.contains("qPkStdEnabled")) {
        wizardForm.addControl(
          "qPkStdEnabled",
          FormBuilder.control(wizardFormValue.qPkStdEnabled || "false")
        );
      }

      // Handle qPkCompAnalysis - restore it to individual form controls
      // This is a simplified version - full restore logic would be similar to Questions.js
      if (
        wizardFormValue.qPkCompAnalysis &&
        Array.isArray(wizardFormValue.qPkCompAnalysis)
      ) {
        wizardFormValue.qPkCompAnalysis.forEach((block, index) => {
          const suffix = index > 0 ? `_${index + 1}` : "";
          // Always use "Comp" prefix for shared fields (mirrors addCompPkBlock)
          const sharedFieldPrefix = "Comp";

          const qPkTableControlName = `qPkTable${sharedFieldPrefix}${suffix}`;
          const initialTable =
            block.qPkTable !== undefined && block.qPkTable !== null
              ? block.qPkTable
              : "";
          if (!wizardForm.contains(qPkTableControlName)) {
            wizardForm.addControl(
              qPkTableControlName,
              FormBuilder.control(initialTable)
            );
          } else {
            wizardForm.get(qPkTableControlName).setValue(initialTable);
          }

          const qPkPopulationControlName = `qPkPopulation${sharedFieldPrefix}${suffix}`;
          const initialPopulation =
            block.qPkPopulation !== undefined && block.qPkPopulation !== null
              ? block.qPkPopulation
              : "";
          if (!wizardForm.contains(qPkPopulationControlName)) {
            wizardForm.addControl(
              qPkPopulationControlName,
              FormBuilder.control(initialPopulation)
            );
          } else {
            wizardForm
              .get(qPkPopulationControlName)
              .setValue(initialPopulation);
          }

          if (block.qPkMultiDose !== undefined && block.qPkMultiDose !== null) {
            const controlName = `qPkMultiDose${sharedFieldPrefix}${suffix}`;
            if (!wizardForm.contains(controlName)) {
              wizardForm.addControl(controlName, FormBuilder.control("false"));
            }
            wizardForm.get(controlName).setValue(String(block.qPkMultiDose));
          }

          // Restore qPkFigures
          if (block.qPkFigures !== undefined && block.qPkFigures !== null) {
            const controlName = `qPkFigures${sharedFieldPrefix}${suffix}`;
            if (!wizardForm.contains(controlName)) {
              wizardForm.addControl(controlName, FormBuilder.control([]));
            }
            const figuresValue = Array.isArray(block.qPkFigures)
              ? block.qPkFigures.filter(
                  item => item !== null && item !== undefined && item !== ""
                )
              : typeof block.qPkFigures === "string" && block.qPkFigures !== ""
                ? [block.qPkFigures]
                : [];
            wizardForm.get(controlName).setValue(figuresValue);
          }

          // Restore qPkFiguresExtra
          if (
            block.qPkFiguresExtra !== undefined &&
            block.qPkFiguresExtra !== null
          ) {
            const qPkFiguresExtraControlName = `qPkFiguresExtra${sharedFieldPrefix}${suffix}`;
            if (!wizardForm.contains(qPkFiguresExtraControlName)) {
              wizardForm.addControl(
                qPkFiguresExtraControlName,
                FormBuilder.control([])
              );
            }
            const figuresExtraValue = Array.isArray(block.qPkFiguresExtra)
              ? block.qPkFiguresExtra.filter(
                  item => item !== null && item !== undefined && item !== ""
                )
              : typeof block.qPkFiguresExtra === "string" &&
                  block.qPkFiguresExtra !== ""
                ? [block.qPkFiguresExtra]
                : [];
            wizardForm
              .get(qPkFiguresExtraControlName)
              .setValue(figuresExtraValue);
          }

          // Restore qPkTimelines array
          const qPkTimelinesControlName = `qPkTimelines${sharedFieldPrefix}${suffix}`;
          populateValueHelperArray(
            wizardForm,
            qPkTimelinesControlName,
            block.qPkTimelines
          );

          // Restore qPkStatsAnalysis
          const qPkStatsAnalysisControlName = `qPkStatsAnalysis${sharedFieldPrefix}${suffix}`;
          if (!wizardForm.contains(qPkStatsAnalysisControlName)) {
            wizardForm.addControl(
              qPkStatsAnalysisControlName,
              FormBuilder.control([])
            );
          }
          if (
            block.qPkStatsAnalysis !== undefined &&
            block.qPkStatsAnalysis !== null
          ) {
            const statsValue = Array.isArray(block.qPkStatsAnalysis)
              ? block.qPkStatsAnalysis.filter(
                  item => item !== null && item !== undefined && item !== ""
                )
              : typeof block.qPkStatsAnalysis === "string" &&
                  block.qPkStatsAnalysis !== ""
                ? [block.qPkStatsAnalysis]
                : [];
            wizardForm.get(qPkStatsAnalysisControlName).setValue(statsValue);
          }

          // Restore Comparative-specific fields
          if (
            block.qPkComparisonName !== undefined &&
            block.qPkComparisonName !== null
          ) {
            const controlName = `qPkComparisonName${suffix}`;
            if (!wizardForm.contains(controlName)) {
              wizardForm.addControl(controlName, FormBuilder.control(""));
            }
            wizardForm.get(controlName).setValue(block.qPkComparisonName);
          }

          // Restore qPkComparisonPairs array
          const qPkComparisonPairsControlName = `qPkComparisonPairs${suffix}`;
          if (
            block.qPkComparisonPairs !== undefined &&
            block.qPkComparisonPairs !== null &&
            Array.isArray(block.qPkComparisonPairs) &&
            block.qPkComparisonPairs.length > 0
          ) {
            if (!wizardForm.contains(qPkComparisonPairsControlName)) {
              wizardForm.addControl(
                qPkComparisonPairsControlName,
                new FormArray([])
              );
            }
            let comparisonPairsArray = wizardForm.get(
              qPkComparisonPairsControlName
            );

            if (!(comparisonPairsArray instanceof FormArray)) {
              wizardForm.setControl(
                qPkComparisonPairsControlName,
                new FormArray([])
              );
              comparisonPairsArray = wizardForm.get(
                qPkComparisonPairsControlName
              );
            }

            while (comparisonPairsArray.length > 0) {
              comparisonPairsArray.removeAt(0);
            }

            const firstItem = block.qPkComparisonPairs[0];
            if (
              typeof firstItem === "object" &&
              firstItem !== null &&
              (firstItem.Analyte !== undefined ||
                firstItem.Comparator !== undefined)
            ) {
              block.qPkComparisonPairs.forEach(pair => {
                const group = FormBuilder.group({
                  Analyte: [pair.Analyte || ""],
                  Comparator: [pair.Comparator || ""],
                });
                comparisonPairsArray.push(group);
              });
            }
          }

          // Restore qPkAnalytes array
          const qPkAnalytesControlName = `qPkAnalytes${suffix}`;
          populateValueHelperArray(
            wizardForm,
            qPkAnalytesControlName,
            block.qPkAnalytes
          );
          // Ensure at least one empty control so the UI shows an input field
          {
            const arr = wizardForm.get(qPkAnalytesControlName);
            if (arr && arr instanceof FormArray && arr.length === 0) {
              const group = FormBuilder.group({ valueHelper: [""] });
              group.meta = { key: `pvh_${qPkAnalytesControlName}_0` };
              arr.push(group);
            }
          }

          // Restore qPkExposure array
          const qPkExposureControlName = `qPkExposure${suffix}`;
          populateValueHelperArray(
            wizardForm,
            qPkExposureControlName,
            block.qPkExposure
          );
          // Ensure at least one empty control so the UI shows an input field
          {
            const arr = wizardForm.get(qPkExposureControlName);
            if (arr && arr instanceof FormArray && arr.length === 0) {
              const group = FormBuilder.group({ valueHelper: [""] });
              group.meta = { key: `pvh_${qPkExposureControlName}_0` };
              arr.push(group);
            }
          }

          // Restore qPkDoseInteraction
          if (
            block.qPkDoseInteraction !== undefined &&
            block.qPkDoseInteraction !== null
          ) {
            const qPkDoseInteractionControlName = `qPkDoseInteraction${suffix}`;
            if (!wizardForm.contains(qPkDoseInteractionControlName)) {
              wizardForm.addControl(
                qPkDoseInteractionControlName,
                FormBuilder.control("false")
              );
            }
            wizardForm
              .get(qPkDoseInteractionControlName)
              .setValue(String(block.qPkDoseInteraction));
          }
        });
        // Trigger re-render so ComparativePKQuestions and dropdown/input see restored values
        setRestoreVersion(prev => prev + 1);
      }
    }
  };

  // function cleanObject(obj) {
  //   for (const key in obj) {
  //     if (obj[key] === null || obj[key] === "") {
  //       return true;
  //     } else if (typeof obj[key] === "object" && cleanObject(obj[key])) {
  //       return true;
  //     }
  //   }
  //   return false;
  // }

  // Use unified submit handler that collects data from all four pages
  const handleSubmit = createUnifiedSubmitHandler({
    wizardForm,
    qForm,
    studyId,
    setAjaxLoading,
    setQuestionsDataSaved,
    questionsDataSaved,
    setFormFilledData,
    setWizardFormFilledData,
    setErrMsg,
    errRef,
    setShowRedirect,
    setIsSaving,
    questionsRef: null, // Comparative PK page doesn't use questionsRef
    CsrAPI,
    toast,
    isProduction,
    Sentry,
  });

  const PageContent = () => {
    return (
      <div className="questions-page">
        <span
          ref={errRef}
          className={errMsg ? "errmsg" : "offscreen"}
          aria-live="assertive">
          {errMsg}
        </span>
        <div className={"questions-content"}>
          <div className={"questions-content__items"}>
            {/* <div className={"questions-content__item"}>
              <div className={"questions-content__label intro-text"}>
                <span className={"questions-content__label-text"}>
                  Please answer all of the questions.
                </span>
              </div>
            </div> */}
            {questionsData.current && (
              <FieldGroup
                control={wizardForm}
                render={() => (
                  <form name={"wizardForm"}>
                    <ComparativePKQuestions
                      globalId={0}
                      wizardForm={wizardForm}
                      questionsData={questionsData.current}
                    />
                  </form>
                )}
              />
            )}
          </div>
        </div>
        <ToastContainer
          position="top-right"
          autoClose={2000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick={true}
          rtl={false}
          pauseOnFocusLoss={false}
          draggable
          pauseOnHover
          theme="light"
        />
      </div>
    );
  };

  if (showRedirect || wrongEnvironment) {
    return <RedirectHandler />;
  }

  return (
    <DashboardContent
      sidebarCSR={true}
      headerTitle={
        "Anwser the following questions related to Comparative Pharmacokinetics"
      }
      headerButton={false}
      pageContent={PageContent()}
      prevButton={{
        buttonTitle: "Standard PK",
        buttonGoToAction: "/csr/" + studyId + "/stdpharmacokinetics",
      }}
      nextButton={{
        buttonTitle: "Bioavailability",
        buttonGoToAction: "/csr/" + studyId + "/bioavailability",
      }}
      saveButtonHandler={handleSubmit}
      saveButton={true}
    />
  );
};

export default ComparativePharmacokinetics;
